import type { ServiceOptionMapper } from './serviceOptionMapper'
import type { ImpressionViewAncillariesItem } from '@/scopes/checkout/domain/analytics/types'
import type { APIBookingDraft, APIBookingDraftService } from '@/scopes/checkout/infrastructure/api/types'
import { ServiceType } from '@/scopes/checkout/domain/types'

export interface GA4ImpressionItemsConfig {
  showToggleForAllParticipants?: boolean
  toggleForAllParticipantsCheckedByDefault?: boolean
  showSpecialLuggage?: boolean
}

const serviceTypeGA4CategoryMap: Partial<Record<ServiceType, string>> = {
  [ServiceType.BookingRedeem]: 'selectBookingRedeemer',
  [ServiceType.Bridge]: 'selectBridge',
  [ServiceType.Bus]: 'selectBus',
  [ServiceType.CancellationInsurance]: 'selectCancellationInsurance',
  [ServiceType.CancellationInsurancePerBooking]: 'selectCancellationInsurance',
  [ServiceType.CarRentalFiltering]: 'selectCarRental',
  [ServiceType.TShirt]: 'selectTShirt',
  [ServiceType.Sweater]: 'selectSweater',
  [ServiceType.DinnerVouchers]: 'select MEAL-VOUCHERS',
  [ServiceType.Golf]: 'selectGolf',
  [ServiceType.Luggage]: 'selectLuggage',
  [ServiceType.TravelInsurance]: 'selectTravelInsurance',
  [ServiceType.TravelInsurancePerBooking]: 'selectTravelInsurance',
  [ServiceType.MealplanDP]: 'selectMealplan',
  [ServiceType.SkiPass]: 'selectSkiPass',
  [ServiceType.SkiMaterial]: 'selectSkiMaterial',
  [ServiceType.SkiLesson]: 'selectSkiLesson',
}

export function getGA4CategoryByServiceType(serviceType: ServiceType, aName: string): string {
  return serviceTypeGA4CategoryMap[serviceType] ?? `select ${aName}`
}

export interface SelectedItemInput {
  label: string
  value: string
  price?: number
  rawPrice?: number
  badge?: string | null
  quantity?: string
}

export interface SelectItemConfig {
  serviceType: ServiceType
  serviceId: string
  step: number
}

/**
 * Maps a normalized array of selected options to GA4 select_item items.
 * Each mapper extracts its selected options, normalizes them into `SelectedItemInput[]`,
 * and delegates here for a consistent output shape.
 * Override individual fields only when the mapper genuinely differs from the default.
 * Does NOT assign `index` — the caller (hook) is responsible.
 */
export function mapDefaultSelectedItemToGA4(
  inputs: SelectedItemInput[],
  config: SelectItemConfig,
): ImpressionViewAncillariesItem[] {
  const ga4Category = getGA4CategoryByServiceType(config.serviceType, config.serviceId)

  return inputs.map(input => ({
    item_name: input.label,
    item_id: input.value,
    price: input.rawPrice ?? input.price ?? 0,
    item_category: `Ancillary > ${input.badge ? 'Persuasion' : 'Additional cost'} > ${ga4Category}`,
    item_category2: ga4Category,
    item_list_name: `checkout${config.step}`,
    quantity: input.quantity ?? '1',
  }))
}

/**
 * Maps API service options to GA4 impression items using the serviceOptionMapper for price calculation.
 * Does NOT assign `index` — the caller (response mapper or hook) is responsible.
 *
 * Quantity logic:
 * - When showToggleForAllParticipants is enabled: toggleForAllParticipantsCheckedByDefault=true → 1 (perParticipant visual),
 *   toggleForAllParticipantsCheckedByDefault=false → appliesToParticipants.length (perBooking visual)
 * - Otherwise (PerParticipant, PerBooking or undefined) → 1
 */
export function mapDefaultGA4ImpressionItems(
  service: APIBookingDraftService,
  bookingDraft: APIBookingDraft,
  serviceOptionMapper: ServiceOptionMapper,
  config?: GA4ImpressionItemsConfig,
  serviceType?: ServiceType,
): ImpressionViewAncillariesItem[] {
  if (!service.options?.length)
    return []

  const ga4Category = serviceType
    ? getGA4CategoryByServiceType(serviceType, service.aName)
    : service.aName

  return service.options.map((option) => {
    const mapped = serviceOptionMapper.map(option, service, bookingDraft)
    const appliesToParticipants = (option as { appliesToParticipants?: number[] }).appliesToParticipants
    return {
      item_name: mapped.label,
      item_id: mapped.value,
      price: mapped.price,
      item_category: `Ancillary > Additional cost > ${ga4Category}`,
      item_category2: ga4Category,
      item_list_name: 'checkout1',
      quantity: mapQuantity(appliesToParticipants, config),
    }
  })
}

function mapQuantity(
  appliesToParticipants: number[] | undefined,
  config?: GA4ImpressionItemsConfig,
): string {
  if (config?.showToggleForAllParticipants) {
    return config.toggleForAllParticipantsCheckedByDefault
      ? '1'
      : String(appliesToParticipants?.length || 1)
  }

  return '1'
}
